import { z } from 'zod';
import { ResourceAccess } from 'src/shared/types/organisation.types';
import { IntegrationId } from 'src/shared/types/integration.types';
import { IntegrationSyncTypes } from 'src/shared/types/shared/FlowIntegrationSharedTypes';
import { CreatorInfo } from 'src/shared/types/user.types';
import { ArrayPrimitives, Primitives } from 'src/shared/types/util.types';

export type Flow = {
  version: number;
  id: string;
  name: string;
  description: string;
  isRunning: boolean;
  creator: CreatorInfo;
  creatorId: string;
  createdAt: number;
  lastUpdated: number;
  executionCount: number;
  integrations: IntegrationId[];
  organisationId: string;
  latest: FlowBlock[];
  stable: FlowBlock[];
  previewImage?: string;
  resourceAccess: ResourceAccess[];
};

export type FlowBlock =
  | StartBlock
  | ActionBlock
  | ConditionBlock
  | ApprovalBlock
  | AppBlock
  | CustomizableBlock
  | ModelBlock;

export interface BaseFlowBlock {
  id: string;
  name: string;
  blockHash: string;
  blockContentHash: string;
  description: string;
  meta: {
    parentId?: string;
    loopStyle?: Record<string, any>;
    icon?: string;
    /** Persisted position for dangling (disconnected) nodes that are not managed by auto-layout */
    position?: { x: number; y: number };
  };
  executionCount: number;
  coverage: number;
  next: string[] | { [key: string]: string[] };
  [key: string]: unknown;
}

export type TriggerType =
  | 'once_per_conversation' // one message per conversation,(sent or received),
  | 'first_message' // first message in a conversation (sent or received)
  | 'first_incoming_message' // first received message in a conversation
  | 'first_outgoing_message' // first sent message in a conversation
  | 'all_new_messages' // all new messages in a conversation, sent and received
  | 'all_incoming_messages' // all incoming messages in a conversation
  | 'all_outgoing_messages' // all outgoing messages in a conversation
  | '_legacy_trigger'; // legacy trigger

export interface StartBlock extends BaseFlowBlock {
  type: 'start';
  blockContent?: {
    trigger?: {
      type?: TriggerType;
      filter?: ConditionGroup;
    };
  };
  next: string[];
}

export interface CustomizableBlock extends BaseFlowBlock {
  type: 'custom';
  blockContent: any;
  next: string[];
}

export interface AppBlock extends BaseFlowBlock {
  type: 'app';
  blockContent: AppBlockContent;
  next: {
    default: string[];
    [channel: string]: string[];
  };
}

export type AppBlockContent = {
  appId: string;
  retries?: number;
  version: number | undefined;
  continueOnError?: boolean;
  estimatedTimeSaved?: number;

  secrets: {
    [secretName: string]: string;
  };

  variables: {
    [parameterId: string]: Primitives | ArrayPrimitives | ActionBlockContent['actionParameters'][];
  };

  integrationOverrides?: {
    /** path to the parameter, examples: "folderId", "fields.0.value". */
    [parameterLocator: string]: {
      [integrationId: string]: Primitives | ArrayPrimitives;
    };
  };
};

export interface ActionBlock extends BaseFlowBlock {
  type: 'action' | 'loop';
  blockContent: ActionBlockContent;
  next: string[];
}

export interface ActionBlockContent {
  actionId: string;
  /** leave empty if the action is in the same integration as the flow trigger */
  isExternal?: boolean;
  integrationId?: string;
  integrationName?: string;
  integrationType?: IntegrationSyncTypes | 'default';
  estimatedTimeSaved?: number;
  continueOnError?: boolean;
  actionParameters: {
    [parameterId: string]: Primitives | ArrayPrimitives | ActionBlockContent['actionParameters'][];
  };
  integrationOverrides?: {
    /** path to the parameter, examples: "folderId", "fields.0.value". */
    [parameterLocator: string]: {
      [integrationId: string]: Primitives | ArrayPrimitives;
    };
  };
}

export interface ConditionBlock extends BaseFlowBlock {
  type: 'condition';
  blockContent: ConditionConfig;
  next: { true: string[]; false: string[] };
}

export type ConditionConfig = {
  conditionType: 'filter' | 'if-else';
  conditionGroup: ConditionGroup;
};

export type ConditionGroup = {
  isGroup: true;
  groupBinop: 'AND' | 'OR';
  conditions: (ConditionGroup | Condition)[];
};

// dataType defines the left operand type, the operator determines the right operand type
export type Condition = {
  isGroup?: false;
  options?: {
    caseSensitive?: boolean;
  };
} & (PrimitiveCondition | PrimitiveToArrayCondition | ArrayToArrayCondition);

export type PrimitiveCondition = {
  leftOperand: `{{${string}}}`;
  operator: StringOperators | NumberOperators | BooleanOperators | DateOperators | PrimitiveNullableOperators;
  rightOperand: string;
  dataType: PrimitiveType;
};

export type PrimitiveToArrayCondition = {
  leftOperand: `{{${string}}}`;
  operator: PrimitiveToArrayOperators;
  rightOperand: string[];
  dataType: PrimitiveType;
};

export type ArrayToArrayCondition = {
  leftOperand: `{{${string}}}`;
  operator: ArrayToArrayOperators | ArrayNullableOperators;
  rightOperand: string[];
  dataType: ArrayType;
};

export interface ApprovalBlock extends BaseFlowBlock {
  type: 'approval';
  blockContent: ApprovalConfig;
  next: { approved: string[]; rejected: string[] };
}

export type ApprovalConfig = {
  valuesToApprove: ApprovalValue[];
};

export type ApprovalValue = {
  value: `{{${string}}}`;
  type: PrimitiveType | ArrayType | Exclude<CustomType, 'custom[]'>;
  label?: string;
  description?: string;
  containsHeader?: boolean;
};

export type ModelBlockContent =
  | {
      modelType: 'extraction';
      modelId: string;
    }
  | {
      modelType: 'classification';
      modelIds: string[];
    };

export interface ModelBlock extends BaseFlowBlock {
  type: 'model';
  blockContent: ModelBlockContent;
  next: string[];
}

export const primitiveTypeSchema = z.enum(['string', 'boolean', 'date', 'email', 'number', 'code']);
export const arrayTypeSchema = z.enum(['string[]', 'number[]', 'date[]', 'email[]']);
export const customTypeSchema = z.enum(['custom', 'custom[]']);
export const allTypesSchema = z.union([primitiveTypeSchema, arrayTypeSchema, customTypeSchema]);

export type PrimitiveType = z.infer<typeof primitiveTypeSchema>;
export type ArrayType = z.infer<typeof arrayTypeSchema>;
export type CustomType = z.infer<typeof customTypeSchema>;
export const primitiveToArrayOperatorsSchema = z.enum(['IN', 'NOT_IN']);
export const arrayToArrayOperatorsSchema = z.enum(['CONTAINS_ANY', 'NOT_CONTAINS_ANY']);

export type PrimitiveToArrayOperators = z.infer<typeof primitiveToArrayOperatorsSchema>;
export type ArrayToArrayOperators = z.infer<typeof arrayToArrayOperatorsSchema>;

export const stringOperatorsSchema = z.enum([
  'EQUALS',
  'NOT_EQUALS',
  'CONTAINS',
  'NOT_CONTAINS',
  'STARTS_WITH',
  'ENDS_WITH',
]);
export const numberOperatorsSchema = z.enum([
  'EQUALS',
  'NOT_EQUALS',
  'GREATER_THAN',
  'GREATER_THAN_OR_EQUALS',
  'LESS_THAN',
  'LESS_THAN_OR_EQUALS',
]);
export const dateOperatorsSchema = z.enum(['BEFORE', 'AFTER']);
export const booleanOperatorsSchema = z.enum(['TRUE', 'FALSE']);

export type StringOperators = z.infer<typeof stringOperatorsSchema>;
export type NumberOperators = z.infer<typeof numberOperatorsSchema>;
export type DateOperators = z.infer<typeof dateOperatorsSchema>;
export type BooleanOperators = z.infer<typeof booleanOperatorsSchema>;

export const primitiveNullableOperatorsSchema = z.enum(['EXISTS', 'DOES_NOT_EXIST']);
export const arrayNullableOperatorsSchema = z.enum(['IS_EMPTY', 'IS_NOT_EMPTY']);

export type PrimitiveNullableOperators = z.infer<typeof primitiveNullableOperatorsSchema>;
export type ArrayNullableOperators = z.infer<typeof arrayNullableOperatorsSchema>;

export const allOperatorsSchema = z.union([
  arrayToArrayOperatorsSchema,
  primitiveToArrayOperatorsSchema,
  stringOperatorsSchema,
  numberOperatorsSchema,
  booleanOperatorsSchema,
  dateOperatorsSchema,
  primitiveNullableOperatorsSchema,
  arrayNullableOperatorsSchema,
]);

export type Operators =
  | ArrayToArrayOperators
  | PrimitiveToArrayOperators
  | StringOperators
  | NumberOperators
  | BooleanOperators
  | DateOperators
  | PrimitiveNullableOperators
  | ArrayNullableOperators;

export type PrimitiveOperators = StringOperators | NumberOperators | BooleanOperators | DateOperators;
